import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, PhoneCall, KeyRound, ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Words } from './motion/Reveal';

const STEPS = [
  { icon: FileText, tagFr: 'Quelques minutes', tagEn: 'A few minutes' },
  { icon: PhoneCall, tagFr: 'Sans engagement', tagEn: 'No commitment' },
  { icon: KeyRound, tagFr: 'Prenez la route', tagEn: 'Hit the road' },
];

const useInView = () => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setSeen(true), { threshold: 0.2 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return [ref, seen];
};

const StepCard = ({ step, meta, i, en, seen }) => {
  const Icon = meta.icon;
  return (
    <div
      className={`relative group rounded-3xl p-7 sm:p-8 bg-white border border-emerald-900/10 hover:border-[#1a5c3b]/40 shadow-[0_8px_30px_rgba(15,61,38,0.06)] overflow-hidden transition-[transform,opacity,border-color,box-shadow] duration-700 hover:-translate-y-2 hover:shadow-[0_24px_60px_rgba(26,92,59,0.16)] ${seen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{ transitionDelay: seen ? `${i * 150}ms` : '0ms' }}
      data-testid={`step-card-${i + 1}`}
    >
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#1a5c3b] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <span className="absolute -top-6 -right-2 text-[9rem] leading-none font-black text-transparent [-webkit-text-stroke:1.5px_rgba(26,92,59,0.2)] select-none pointer-events-none group-hover:[-webkit-text-stroke:1.5px_rgba(26,92,59,0.4)] transition-all duration-500">
        {String(i + 1).padStart(2, '0')}
      </span>
      <div className="relative flex items-center gap-3 mb-8">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-[#1a5c3b] flex items-center justify-center shadow-lg shadow-[#1a5c3b]/30 group-hover:scale-110 group-hover:rotate-[-6deg] transition-transform duration-300">
          <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
        </div>
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#1a5c3b] bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
          {en ? meta.tagEn : meta.tagFr}
        </span>
      </div>
      <p className="relative text-emerald-600 text-xs font-bold uppercase tracking-[0.2em] mb-2">{en ? 'Step' : 'Étape'} {i + 1}</p>
      <h3 className="relative text-xl sm:text-2xl font-bold text-[#0f172a] mb-3 leading-snug">{en ? step.titleEn : step.titleFr}</h3>
      <p className="relative text-slate-600 leading-relaxed text-sm sm:text-base">{en ? step.descEn : step.descFr}</p>
    </div>
  );
};

export const ThreeStepsSection = ({ steps, ctaLabel }) => {
  const { language } = useLanguage();
  const en = language === 'en';
  const [ref, seen] = useInView();
  return (
    <section ref={ref} className="relative bg-gradient-to-b from-[#f8faf7] via-[#eef6f0] to-[#f8faf7] py-20 lg:py-28 overflow-hidden" data-testid="steps-section-container">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(26,92,59,0.12),transparent_60%)] pointer-events-none" />
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none bg-[linear-gradient(rgba(26,92,59,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(26,92,59,0.5)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`max-w-3xl mb-14 lg:mb-16 transition-[opacity,transform] duration-700 ${seen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.25em] uppercase text-[#1a5c3b] bg-white border border-emerald-200 shadow-sm px-4 py-1.5 rounded-full mb-6" data-testid="steps-eyebrow">
            <Sparkles className="w-3.5 h-3.5" />{en ? 'Simple process' : 'Processus simple'}
          </span>
          <Words parts={[[en ? 'Your car loan in ' : 'Votre crédit auto en '], [en ? '3 simple steps' : '3 étapes simples', 'text-transparent bg-clip-text bg-gradient-to-r from-[#1a5c3b] to-emerald-500']]} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]" data-testid="steps-heading" />
          <p className="text-base sm:text-lg text-slate-600 mt-5 max-w-2xl">
            {en ? 'From your request to the keys in your hand, we guide you every step of the way.' : 'De votre demande jusqu’à la remise des clés, on vous accompagne à chaque étape.'}
          </p>
        </div>
        <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="hidden lg:block absolute top-[3.75rem] left-[8%] right-[8%] h-px bg-gradient-to-r from-[#1a5c3b]/0 via-[#1a5c3b]/30 to-[#1a5c3b]/0" />
          {steps.map((s, i) => <StepCard key={i} step={s} meta={STEPS[i]} i={i} en={en} seen={seen} />)}
        </div>
        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <Link
            to="/demander-un-pret-auto"
            className="group inline-flex items-center gap-3 bg-[#1a5c3b] hover:bg-[#165432] text-white font-extrabold text-base md:text-lg px-8 py-4 rounded-full shadow-[0_10px_40px_rgba(26,92,59,0.3)] hover:shadow-[0_14px_50px_rgba(26,92,59,0.45)] hover:scale-[1.03] transition-[transform,background-color,box-shadow] duration-300"
            data-testid="steps-cta-button"
          >
            {ctaLabel}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <p className="text-sm text-slate-500">{en ? 'Free and no commitment.' : 'Gratuit et sans engagement.'}</p>
        </div>
      </div>
    </section>
  );
};
